import { Check } from 'lucide-react'
import { Progress } from '@/components/ui/progress'
import type { AuditionWizardStep } from '@/lib/audition/steps'
import { WIZARD_STEPS } from '@/lib/audition/steps'
import { cn } from '@/lib/utils'

interface StageRailProps {
  /** 0-based index into `WIZARD_STEPS` of the step being shown. */
  stepIndex: number
  /**
   * Injectable for tests and for nothing else — production always renders
   * the real list, which is what makes the rail derived rather than a
   * hand-maintained set of labels.
   */
  steps?: readonly AuditionWizardStep[]
}

/**
 * The named stepper across the top of the /apply wizard (LG-898): every
 * step by name, with a progress line running through them.
 *
 * Labels come from `WIZARD_STEPS`, so a step added to that list appears
 * here automatically — there is deliberately no label array to maintain
 * beside it.
 *
 * Below `sm` the names do not fit on one line, so the rail collapses to
 * "Step N of M — {name}" plus the same bar the wizard has always shown.
 * Both layouts are always in the DOM and swapped by CSS, so no viewport
 * measurement (and no hydration mismatch) is involved.
 */
export function StageRail({ stepIndex, steps = WIZARD_STEPS }: StageRailProps) {
  const total = steps.length
  // An empty list has no step to mark and no counter to render, so there is
  // nothing to degrade to — bail rather than let the clamp below produce
  // index -1 and blow up on `currentStepDef.name`.
  if (total === 0) return null
  // Clamped rather than trusted: `stepIndex` is derived from a server-sent
  // currentStep, and an out-of-range value must degrade to an end of the
  // rail, never render a rail with nothing marked current.
  const current = Math.min(Math.max(stepIndex, 0), total - 1)
  const currentStepDef = steps[current]
  // The midpoint of the step you are on: you have finished `current` steps
  // and are partway through this one.
  //
  // Both of the obvious formulas lie. `current / (total - 1)` — the one on
  // the design canvas — reads 0% on the step you just started, and 100% on
  // the last step while its fields are still blank. `(current + 1) / total`
  // fixes only the first of those: on a 6-step rail it still reads 6/6 on
  // the blank account step, exactly the bug it was meant to remove. The rail
  // is only ever on screen DURING the wizard, so there is no honest moment
  // for this bar to read 100% at all.
  const percent = ((current + 0.5) / total) * 100

  return (
    <div>
      {/* Mobile: name + counter + the original bar. */}
      <div className="space-y-2 sm:hidden">
        <p className="text-sm text-muted-foreground">
          {`Step ${current + 1} of ${total} — ${currentStepDef.name}`}
        </p>
        <Progress value={percent} />
      </div>

      {/* Desktop: the full named rail. A list, because it is one — the
          progress line is drawn behind the markers rather than as items,
          so screen readers get the step names and nothing else. */}
      <ol
        aria-label="Application progress"
        className="hidden sm:flex sm:items-start sm:justify-between sm:gap-1"
      >
        {steps.map((step, index) => {
          const done = index < current
          const isCurrent = index === current
          return (
            <li
              key={step.id}
              className="relative flex flex-1 flex-col items-center gap-2"
              aria-current={isCurrent ? 'step' : undefined}
            >
              {/* Connector to the previous marker. Drawn by each step
                  except the first, so the line can never outrun the rail
                  at either end. Sits behind the marker (-z-10) and is
                  decorative. */}
              {index > 0 && (
                <span
                  aria-hidden="true"
                  className={cn(
                    '-z-10 absolute top-3.5 right-1/2 left-[-50%] h-0.5',
                    done || isCurrent ? 'bg-primary' : 'bg-border',
                  )}
                />
              )}
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-7 items-center justify-center rounded-full border-2 bg-background font-medium text-xs transition-colors',
                  done && 'border-primary bg-primary text-primary-foreground',
                  isCurrent && 'border-primary text-primary',
                  !done && !isCurrent && 'border-border text-muted-foreground',
                )}
              >
                {done ? <Check className="size-4" /> : index + 1}
              </span>
              {/* The numeral/check marker is aria-hidden and the "Step N of
                  M" counter lives in the sm:hidden block, so on desktop a
                  screen reader would otherwise have to count list items to
                  learn where it is.

                  A visually hidden span and not an `aria-label` on the
                  <li>: `listitem` is a container role, so an author name
                  does not replace the item's contents — NVDA and JAWS
                  announce the label and then still walk into the name span,
                  reading "Step 3 of 6, Goals … Goals". And there is no
                  double-read to avoid on the mobile counter, which is
                  `display: none` at `sm` and up and therefore out of the
                  accessibility tree entirely. */}
              {isCurrent && (
                <span className="sr-only">{`Step ${current + 1} of ${total}, `}</span>
              )}
              <span
                className={cn(
                  'text-center text-xs leading-tight',
                  isCurrent
                    ? 'font-medium text-foreground'
                    : 'text-muted-foreground',
                )}
              >
                {step.name}
              </span>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
